import { useEffect, useState } from 'react'
import { X, Plus, Trash2, MessageSquare, Search } from 'lucide-react'
import { useInterviewStore } from '@/stores/useInterviewStore'
import { cn } from '@/lib/utils'

export function SessionPicker() {
  const {
    sessions,
    isLoadingSessions,
    showSessionPicker,
    closeSessionPicker,
    fetchSessions,
    createNewSession,
    loadSessionFromDb,
    deleteSession,
  } = useInterviewStore()

  const [searchQuery, setSearchQuery] = useState('')
  const [newSessionName, setNewSessionName] = useState('')
  const [showNewSession, setShowNewSession] = useState(false)

  useEffect(() => {
    if (showSessionPicker) {
      fetchSessions()
    }
  }, [showSessionPicker, fetchSessions])

  if (!showSessionPicker) return null

  const filteredSessions = sessions.filter((session) =>
    session.name.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const handleCreateSession = () => {
    createNewSession(newSessionName || undefined)
    setNewSessionName('')
    setShowNewSession(false)
  }

  const handleDeleteSession = (e: React.MouseEvent, sessionId: string) => {
    e.stopPropagation()
    if (confirm('Delete this session? This cannot be undone.')) {
      deleteSession(sessionId)
    }
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'No messages'
    const date = new Date(dateStr)
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))

    if (days === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    } else if (days === 1) {
      return 'Yesterday'
    } else if (days < 7) {
      return date.toLocaleDateString([], { weekday: 'short' })
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' })
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-20">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={closeSessionPicker}
      />

      {/* Modal */}
      <div className="relative w-full max-w-lg bg-background border border-border rounded-lg shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h2 className="text-sm font-medium">Sessions</h2>
          <button
            onClick={closeSessionPicker}
            className="p-1 text-muted-foreground hover:text-foreground rounded"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Search */}
        <div className="px-4 py-3 border-b border-border">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search sessions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm bg-muted/50 border-0 rounded-md focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </div>
        </div>

        {/* New Session */}
        <div className="px-4 py-3 border-b border-border">
          {showNewSession ? (
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Session name (optional)"
                value={newSessionName}
                onChange={(e) => setNewSessionName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreateSession()}
                className="flex-1 px-3 py-2 text-sm bg-muted/50 border-0 rounded-md focus:outline-none focus:ring-1 focus:ring-ring"
                autoFocus
              />
              <button
                onClick={handleCreateSession}
                className="px-3 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
              >
                Create
              </button>
              <button
                onClick={() => setShowNewSession(false)}
                className="px-3 py-2 text-sm text-muted-foreground hover:text-foreground"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowNewSession(true)}
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-md transition-colors"
            >
              <Plus className="w-4 h-4" />
              New Session
            </button>
          )}
        </div>

        {/* Session List */}
        <div className="max-h-80 overflow-y-auto">
          {isLoadingSessions ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              Loading sessions...
            </div>
          ) : filteredSessions.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              {searchQuery ? 'No sessions match your search' : 'No sessions yet. Create one to get started!'}
            </div>
          ) : (
            <div className="py-2">
              {filteredSessions.map((session) => (
                <div
                  key={session.session_id}
                  onClick={() => loadSessionFromDb(session.session_id)}
                  className="group flex items-center gap-3 px-4 py-3 hover:bg-muted/50 cursor-pointer transition-colors"
                >
                  <div className="flex-shrink-0 w-8 h-8 bg-muted rounded-md flex items-center justify-center">
                    <MessageSquare className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">
                      {session.name}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span>{session.total_turns} turns</span>
                      <span>·</span>
                      <span>{formatDate(session.last_message_at)}</span>
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <span
                      className={cn(
                        'px-2 py-0.5 text-xs rounded-full',
                        session.mode === 'brainstorm' && 'bg-blue-100 text-blue-700',
                        session.mode === 'foolscap' && 'bg-purple-100 text-purple-700',
                        session.mode === 'draft' && 'bg-green-100 text-green-700'
                      )}
                    >
                      {session.mode}
                    </span>
                  </div>
                  <button
                    onClick={(e) => handleDeleteSession(e, session.session_id)}
                    className="flex-shrink-0 p-1.5 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
